import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { useUpdateVersion } from "../../../../../domain/Version";
import {
  View,
  useRecursiveDeleteView,
  useUpdateView,
} from "../../../../../domain/View";
import { ManagedTransaction } from "../../../../../firebase/firestore";
import { useAppNotificationManager } from "../../../../AppProvider/AppNotificationProvider";
import { useVersions } from "../../../../InOrganizationProvider/VersionsProvider";
import { useBulkDialogRerenderGuard } from "../../BulkDialogRerenderGuardProvider";
import { getDialogMessages } from "../../helper";

/**
 * DialogViewDeleteSome
 *
 * BulkDialogRerenderGuardProvider 内で使用
 * Firestore 更新による再レンダリングからダイアログ状態を保護する。
 */
export const DialogViewDeleteSome: React.FC<{
  views: View[];
  selectedViewIds: string[];
  setSelectedViewIds: React.Dispatch<React.SetStateAction<string[]>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ views, selectedViewIds, setSelectedViewIds, open, setOpen }) => {
  const navigate = useNavigate();
  const { organizationId, versionId, viewId: nowViewId } = useAppRouteParams();
  const recursiveDeleteView = useRecursiveDeleteView();
  const updateView = useUpdateView();
  const updateVersion = useUpdateVersion();
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);
  const { showErrorNotification } = useAppNotificationManager();

  const { openBulkDialog, isBulkDialogOpen } = useBulkDialogRerenderGuard();

  const selectedViews = views.filter(({ id }) => selectedViewIds.includes(id));

  const messages = getDialogMessages({
    isSome: true,
    actionType: "delete",
    targetType: "view",
  });

  const runDelete = useCallback(async (view: View) => {
    const deleteQueue = await recursiveDeleteView(view.id, {});
    await deleteQueue.exec();
  }, []);

  const onCloseHandler = useCallback(
    async (states: Array<View & { status: string }>) => {
      setOpen(false);
      const [successIds, remainingIds] = states.reduce<[string[], string[]]>(
        ([success, remaining], s) => {
          s.status === "success" ? success.push(s.id) : remaining.push(s.id);
          return [success, remaining];
        },
        [[], []]
      );
      setSelectedViewIds(remainingIds);

      // ソート番号を振り直す（成功した削除がある場合のみ）
      if (successIds.length > 0) {
        const otherViews = views.filter(({ id }) => !successIds.includes(id));
        try {
          await ManagedTransaction.runTransaction(async (transaction) => {
            for (const [index, view] of otherViews.entries()) {
              if (view.sort !== index) {
                await updateView(
                  { ...view, sort: index },
                  { viewId: view.id },
                  { transaction }
                );
              }
            }
          });
        } catch (error: unknown) {
          showErrorNotification(
            "ビューのソート番号の更新に失敗しました",
            error
          );
        }

        // 削除に成功したビューが初期ビューに設定されていた場合、初期ビュー設定を解除する
        if (
          version &&
          version.initialViewId &&
          successIds.includes(version.initialViewId)
        ) {
          try {
            await updateVersion({ ...version, initialViewId: "" }, {}, {});
          } catch (error: unknown) {
            showErrorNotification("初期ビュー設定の解除に失敗しました", error);
          }
        }
      }

      // 削除に成功したビューに現在表示中のビューが含まれていたらナビゲート
      if (nowViewId && successIds.includes(nowViewId)) {
        navigate(`/organizations/${organizationId}/versions/${versionId}`);
      }
    },
    [views, nowViewId, organizationId, versionId, version, updateVersion]
  );

  const includesInitialView =
    !!version?.initialViewId && selectedViewIds.includes(version.initialViewId);

  const bulkDialogArgs = useMemo(
    () => ({
      ...messages.confirm,
      displayMessage: includesInitialView ? (
        <div style={{ marginTop: 4, color: "red" }}>
          初期ビューに設定されているビューが含まれています。削除すると設定が解除されます。
        </div>
      ) : undefined,
      rows: selectedViews,
      rowKey: "id" as const,
      run: runDelete,
      isThemeRed: true,
      renderItem: (view: View) => <div>{view.title}</div>,
      onClose: ({ states }: { states: Array<View & { status: string }> }) =>
        onCloseHandler(states),
    }),
    [messages, selectedViews, runDelete, onCloseHandler, includesInitialView]
  );

  const argsRef = useRef(bulkDialogArgs);
  argsRef.current = bulkDialogArgs;

  useEffect(() => {
    if (open && !isBulkDialogOpen) {
      openBulkDialog<"id", View>(argsRef.current);
    }
  }, [open, isBulkDialogOpen, openBulkDialog]);

  return null;
};
